import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, CheckCircle, Database, Mail, Server } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Input } from '../../components/Input';
import { Stepper } from '../../components/Stepper';
import { OptionCard } from '../../components/OptionCard';
import { PageHeader } from '../../components/PageHeader';
import { Money } from '../../components/Money';
import { apiPost } from '../../api/client';
import { useSellablePackages, useSellablePrices } from '../../api/catalog';
import { useBrand } from '../../brand/BrandProvider';
import { useSession } from '../../stores/session';
import { toMajor } from '../../contracts/money';

const STEPS = ['Owner & business', 'Plan & database', 'Initial payment'];

const limitText = (v: number | 'unlimited') => (v === 'unlimited' ? 'Unlimited' : v);

/** 3-step wizard for creating a customer business under the reseller. */
export function CustomerCreate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [mobile, setMobile] = useState('+880');
  const [businessName, setBusinessName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  // Invite-email delivery is a follow-up, so the temporary-password path is the active
  // method for now (the invite card is disabled below). The admin sets a password and
  // shares it with the customer.
  const [passwordMethod, setPasswordMethod] = useState<'invite' | 'manual'>('manual');
  const [tempPassword, setTempPassword] = useState('');

  // Step 2 — plan catalog, scoped SERVER-SIDE to the catalog this host sells: on a
  // reseller domain that is the reseller's own packages, on a platform domain the
  // platform catalog. RLS alone would leak the other catalogs in, and the DTO's
  // `soldBy` says only *whether* a package is reseller-owned, never *which* — so
  // the host has to do the scoping. Matches GET /pricing, hence the public pricing
  // page and this picker can never disagree.
  const { data: packages = [] } = useSellablePackages();
  const { data: prices = [] } = useSellablePrices();
  // Reused for platform-admin direct-create too (route /admin/businesses/new); the backend
  // makes it a direct business for a platform actor. Copy + post-create nav adapt by role.
  const isAdmin = useSession((s) => s.activeRole) === 'admin';
  const sellable = packages.filter((p) => p.status === 'published' && p.visibility === 'public');

  // Billing interval toggle (Monthly / Yearly).
  const [interval, setInterval] = useState<'month' | 'year'>('month');

  // Resolve the price for a given package + billing interval. Falls back gracefully to
  // the opposite interval when no price exists for the chosen one — prevents ৳0 / crash.
  const resolvePrice = (pkgId: string, ivl: 'month' | 'year') => {
    const opposite = ivl === 'month' ? 'year' : 'month';
    return (
      prices.find((pr) => pr.packageId === pkgId && pr.interval === ivl) ??
      prices.find((pr) => pr.packageId === pkgId && pr.interval === opposite) ??
      null
    );
  };

  // Minor-unit amount for the plan card display.
  const priceForInterval = (pkgId: string) => resolvePrice(pkgId, interval)?.amount ?? 0;

  // Per-card interval label (/mo or /yr) — follows the interval that was actually resolved.
  const resolvedIntervalLabel = (pkgId: string): string => {
    const hasChosen = prices.some((pr) => pr.packageId === pkgId && pr.interval === interval);
    const actual: 'month' | 'year' = hasChosen ? interval : interval === 'month' ? 'year' : 'month';
    return actual === 'month' ? '/mo' : '/yr';
  };

  const [planId, setPlanId] = useState('');
  useEffect(() => {
    const first = sellable[0];
    if (!planId && first) setPlanId(first.id);
  }, [sellable, planId]);

  const [trialDays, setTrialDays] = useState(7);
  // DB mode — the reseller's policy decides which of the 3 are offered (decision #21).
  const brand = useBrand();
  const allowedModes = brand.allowedDbModes ?? ['shared'];
  // Shared is the only DB mode wired end-to-end today (managed/self_hosted have no login
  // serve-path yet — they're disabled below), so it's the default for everyone.
  const [dbMode, setDbMode] = useState<'shared' | 'managed' | 'self_hosted'>('shared');
  // BYOD (self_hosted) connection — sent as selfHostedCreds; the API verifies + seals it.
  const [byodHost, setByodHost] = useState('');
  const [byodPort, setByodPort] = useState('3306');
  const [byodDatabase, setByodDatabase] = useState('');
  const [byodUsername, setByodUsername] = useState('');
  const [byodPassword, setByodPassword] = useState('');

  // Derived from step-2 selections — the resolved price for the selected plan + interval.
  // Used in step 3 for the default cash amount and the live remaining/advance status line.
  const selectedPrice = planId ? resolvePrice(planId, interval) : null;
  const selectedPriceMinor = selectedPrice?.amount ?? 0;
  const selectedPriceMajor = toMajor(selectedPriceMinor);

  // Step 3
  const [paymentMode, setPaymentMode] = useState<'online' | 'cash'>('online');
  // cashAmount is stored in major units (the ৳ input shows whole-taka values).
  // Defaulted to the selected plan price; synced whenever the plan or interval changes.
  const [cashAmount, setCashAmount] = useState('300');
  const [cashReceipt, setCashReceipt] = useState('');
  const [sendReceipt, setSendReceipt] = useState(true);

  // Keep the cash-amount default in sync with the selected plan + interval.
  // Re-syncs when the plan or billing interval changes (e.g. user navigates back to step 2).
  useEffect(() => {
    if (!planId || prices.length === 0) return;
    const p =
      prices.find((pr) => pr.packageId === planId && pr.interval === interval) ??
      prices.find((pr) => pr.packageId === planId);
    if (p && p.amount > 0) setCashAmount(String(toMajor(p.amount)));
  }, [planId, interval, prices]);

  async function next() {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }
    // Final step → create the business under the reseller (POST /businesses scopes to
    // the reseller actor). Plan/db-mode/payment are richer follow-on flows (subscription
    // + provisioning); this creates the business + records the owner email.
    if (!businessName.trim() || !ownerEmail.trim()) {
      setError('Business name and owner email are required.');
      setStep(0);
      return;
    }
    if (passwordMethod === 'manual' && tempPassword.trim().length < 8) {
      setError('Set a temporary password of at least 8 characters for the owner.');
      setStep(0);
      return;
    }
    if (dbMode === 'self_hosted' && (!byodHost.trim() || !byodDatabase.trim() || !byodUsername.trim() || !byodPassword.trim())) {
      setError('Self-hosted database needs host, database, username and password.');
      setStep(1);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiPost('/businesses', {
        name: businessName.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerName: ownerName.trim() || undefined,
        ownerPhone: mobile.trim() || undefined,
        passwordMethod,
        tempPassword: passwordMethod === 'manual' ? tempPassword : undefined,
        subtitle: subdomain.trim() || undefined,
        dbMode,
        selfHostedCreds:
          dbMode === 'self_hosted'
            ? { host: byodHost.trim(), port: Number(byodPort) || 3306, dbName: byodDatabase.trim(), username: byodUsername.trim(), password: byodPassword }
            : undefined,
        // Billing inputs — wired to the backend POST /businesses billing contract.
        priceId: selectedPrice?.id,
        interval,
        trialDays,
        paymentMode,
        // initialAmountMinor sent only for cash payments; convert major→minor (×100).
        initialAmountMinor: paymentMode === 'cash' ? Math.round(Number(cashAmount) * 100) : undefined,
        cashReceipt: paymentMode === 'cash' && cashReceipt ? cashReceipt : undefined,
      });
      navigate({ to: isAdmin ? '/admin/businesses' : '/reseller/customers' });
    } catch {
      setError(`Could not create the ${isAdmin ? 'business' : 'customer'}. Please try again.`);
      setBusy(false);
    }
  }
  function back() {
    if (step > 0) setStep(step - 1);
  }

  const subdomainAvailable = subdomain.length >= 3 && /^[a-z0-9-]+$/.test(subdomain);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-2xl mx-auto">
      <PageHeader
        eyebrow={isAdmin ? 'Businesses' : 'Customers'}
        title={isAdmin ? 'Create a business' : 'Create a customer'}
        subtitle={isAdmin ? 'Creates a direct business on the platform.' : 'Creates a business under your reseller account.'}
      />

      <div className="mb-6">
        <Stepper steps={STEPS} current={step} />
      </div>

      <Card className="p-6 sm:p-8">
        {step === 0 && (
          <div className="space-y-5">
            <CardHeader title="Owner & business" subtitle="Who's the primary owner and what's the business called?" />
            <div className="grid sm:grid-cols-2 gap-5">
              <Input
                label="Owner email"
                required
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
              />
              <Input
                label="Owner full name"
                required
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
              />
              <Input
                label="Mobile (BD)"
                required
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
              <Input
                label="Business name"
                required
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </div>

            <Input
              label="Subdomain"
              required
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              suffix={
                subdomainAvailable ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Available
                  </span>
                ) : null
              }
              hint={`${subdomain || 'your-business'}.itcitysoft.com`}
            />

            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                How will the customer set their password?
              </div>
              <div className="space-y-2.5">
                <OptionCard
                  selected={passwordMethod === 'manual'}
                  onSelect={() => setPasswordMethod('manual')}
                  asLabel={false}
                >
                  <div className="flex items-start gap-3">
                    <Server className="w-5 h-5 text-brand dark:text-brand-dm flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                        I'll set a temporary password
                      </div>
                      <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                        You set one and share it with the customer. They can change it later.
                      </p>
                    </div>
                  </div>
                </OptionCard>
                <OptionCard selected={false} onSelect={() => {}} disabled asLabel={false}>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-zinc-400 dark:text-zinc-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                        Send invitation email <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">(coming soon)</span>
                      </div>
                      <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                        They click a link and set their own password.
                      </p>
                    </div>
                  </div>
                </OptionCard>
              </div>

              {passwordMethod === 'manual' && (
                <Input
                  label="Temporary password"
                  type="text"
                  className="mt-3"
                  placeholder="At least 8 characters"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  hint="Share this with the customer — they can change it after their first sign-in."
                />
              )}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <CardHeader title="Plan & database" subtitle="Pick a starting plan and database mode." />

            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Plan</div>
                {/* Monthly / Yearly billing toggle — mirrors the public Pricing page pill style. */}
                <div className="inline-flex items-center gap-1 p-1 rounded-full border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                  <button
                    type="button"
                    onClick={() => setInterval('month')}
                    className={
                      'px-4 py-1.5 rounded-full text-sm font-medium transition ' +
                      (interval === 'month'
                        ? 'bg-white dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50 shadow-sm'
                        : 'text-zinc-500 dark:text-zinc-400')
                    }
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setInterval('year')}
                    className={
                      'px-4 py-1.5 rounded-full text-sm font-medium transition ' +
                      (interval === 'year'
                        ? 'bg-white dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50 shadow-sm'
                        : 'text-zinc-500 dark:text-zinc-400')
                    }
                  >
                    Yearly
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {sellable.map((p) => (
                  <OptionCard
                    key={p.id}
                    selected={planId === p.id}
                    onSelect={() => setPlanId(p.id)}
                    asLabel={false}
                    className="p-4"
                  >
                    <div>
                      <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">{p.name}</div>
                      <div className="mt-1 flex items-baseline gap-1">
                        <Money minor={priceForInterval(p.id)} hideCents tone="neutral" className="font-semibold text-zinc-950 dark:text-zinc-50" />
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">{resolvedIntervalLabel(p.id)}</span>
                      </div>
                      <p className="mt-1.5 text-[11px] text-zinc-600 dark:text-zinc-300 leading-snug">
                        {limitText(p.limits.locations)} location{p.limits.locations === 1 ? '' : 's'} · {limitText(p.limits.users)} users
                      </p>
                    </div>
                  </OptionCard>
                ))}
              </div>
            </div>

            <Input
              label="Trial days"
              type="number"
              min={0}
              max={30}
              value={trialDays}
              onChange={(e) => setTrialDays(Number(e.target.value))}
              hint="0 = no trial, charge immediately"
              className="max-w-[160px]"
            />

            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Database</div>
              <div className="space-y-2.5">
                {allowedModes.includes('shared') && (
                  <OptionCard selected={dbMode === 'shared'} onSelect={() => setDbMode('shared')} asLabel={false}>
                    <div className="flex items-start gap-3">
                      <Database className="w-5 h-5 text-brand dark:text-brand-dm flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                          Shared <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">(recommended)</span>
                        </div>
                        <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                          Runs on our shared database — fastest to set up, no extra cost.
                        </p>
                      </div>
                    </div>
                  </OptionCard>
                )}
                {allowedModes.includes('managed') && (
                  <OptionCard selected={dbMode === 'managed'} onSelect={() => { if (isAdmin) setDbMode('managed'); }} disabled={!isAdmin} asLabel={false}>
                    <div className="flex items-start gap-3">
                      <Database className={'w-5 h-5 flex-shrink-0 mt-0.5 ' + (isAdmin ? 'text-brand dark:text-brand-dm' : 'text-zinc-400 dark:text-zinc-500')} />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                          Isolated (dedicated database){!isAdmin && <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400"> (coming soon)</span>}
                        </div>
                        <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                          We provision a dedicated MySQL database in our infrastructure, served at its own subdomain.
                        </p>
                      </div>
                    </div>
                  </OptionCard>
                )}
                {allowedModes.includes('self_hosted') && (
                  <OptionCard selected={false} onSelect={() => {}} disabled asLabel={false}>
                    <div className="flex items-start gap-3">
                      <Server className="w-5 h-5 text-zinc-400 dark:text-zinc-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                          Customer's own MySQL <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">(coming soon)</span>
                        </div>
                        <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                          We connect over TLS to a database the customer hosts themselves.
                        </p>
                      </div>
                    </div>
                  </OptionCard>
                )}
              </div>

              {dbMode === 'self_hosted' && (
                <div className="mt-3 grid sm:grid-cols-2 gap-3 rounded-lg border border-zinc-200 dark:border-zinc-700 p-4">
                  <Input label="DB host" placeholder="db.customer.com" value={byodHost} onChange={(e) => setByodHost(e.target.value)} required />
                  <Input label="Port" value={byodPort} onChange={(e) => setByodPort(e.target.value)} />
                  <Input label="Database name" placeholder="customer_pos" value={byodDatabase} onChange={(e) => setByodDatabase(e.target.value)} required />
                  <Input label="Username" value={byodUsername} onChange={(e) => setByodUsername(e.target.value)} required />
                  <Input label="Password" type="password" value={byodPassword} onChange={(e) => setByodPassword(e.target.value)} required className="sm:col-span-2" />
                  <p className="sm:col-span-2 text-[11px] text-zinc-500 dark:text-zinc-400">
                    We'll connect over TLS and set up the POS schema. Credentials are encrypted at rest.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <CardHeader title="Initial payment" subtitle="How will the first invoice be paid?" />

            <div className="space-y-2.5">
              <OptionCard
                selected={paymentMode === 'online'}
                onSelect={() => setPaymentMode('online')}
                asLabel={false}
              >
                <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                  Customer will pay online when they activate
                </div>
                <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                  We send them an SSLCommerz checkout link with the invitation email.
                </p>
              </OptionCard>
              <OptionCard
                selected={paymentMode === 'cash'}
                onSelect={() => setPaymentMode('cash')}
                asLabel={false}
              >
                <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">
                  I collected cash today — record now
                </div>
                <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5">
                  We mark the first invoice paid and start the subscription clock immediately.
                </p>
              </OptionCard>
            </div>

            {paymentMode === 'cash' && (
              <div className="space-y-4 p-5 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
                <div className="space-y-1">
                  <Input
                    label="Cash amount (BDT)"
                    type="number"
                    min={0}
                    prefix={<span className="text-sm">৳</span>}
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                  />
                  {/* Live remaining / advance credit / paid-in-full status line. */}
                  {(() => {
                    const entered = Number(cashAmount) || 0;
                    const diff = entered - selectedPriceMajor;
                    if (diff === 0) {
                      return (
                        <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                          Paid in full
                        </p>
                      );
                    }
                    if (diff < 0) {
                      return (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          Remaining due: ৳{(selectedPriceMajor - entered).toLocaleString('en-US')}
                        </p>
                      );
                    }
                    return (
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        Credit banked: ৳{(entered - selectedPriceMajor).toLocaleString('en-US')}
                      </p>
                    );
                  })()}
                </div>
                <Input
                  label="Cash receipt #"
                  placeholder="Office receipt #4422"
                  value={cashReceipt}
                  onChange={(e) => setCashReceipt(e.target.value)}
                />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendReceipt}
                    onChange={(e) => setSendReceipt(e.target.checked)}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  <span className="text-sm text-zinc-700 dark:text-zinc-200">Send receipt email</span>
                </label>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-6 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3">
          {step === 0 ? (
            <span />
          ) : (
            <Button variant="ghost" type="button" onClick={back} iconLeft={<ArrowLeft className="w-4 h-4" />}>
              Back
            </Button>
          )}
          <Button onClick={next} disabled={busy} iconRight={<ArrowRight className="w-4 h-4" />}>
            {busy ? 'Creating…' : step === STEPS.length - 1 ? (isAdmin ? 'Create business' : 'Create customer') : 'Continue'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
